import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Camera, DollarSign, RefreshCcw, ImageIcon, TrendingUp, Package } from "lucide-react";

interface KpiActualsSectionProps {
  metrics?: {
    commercialDeployed: number;
    commercialOnOrder: number;
    camerasTrial: number;
    camerasDeployed: number;
    revenueYTD: number;
    arr: number;
    imagesPurchased: number;
    imagesConsumed: number | null;
  };
}

export const KpiActualsSection = ({ metrics }: KpiActualsSectionProps) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="inline-block w-3 h-3 rounded-full bg-base-beige border border-base-beige/50" />
        <h2 className="text-2xl font-semibold text-foreground">Actuals (Delivered/Completed)</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Commercial Deployed
            </CardTitle>
            <Camera className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {formatNumber(metrics?.commercialDeployed || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Shipped to customers
            </p>
          </CardContent>
        </Card>

        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Commercial On-Order
            </CardTitle>
            <Package className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {formatNumber(metrics?.commercialOnOrder || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Awaiting fulfillment
            </p>
          </CardContent>
        </Card>

        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Trial Cameras
            </CardTitle>
            <Camera className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {formatNumber(metrics?.camerasTrial || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Demo deployments
            </p>
          </CardContent>
        </Card>

        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Revenue YTD
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {formatCurrency(metrics?.revenueYTD || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              ARR (Credit Revenue)
            </CardTitle>
            <RefreshCcw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {formatCurrency(metrics?.arr || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Images Purchased
            </CardTitle>
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {formatNumber(metrics?.imagesPurchased || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-base-beige">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Images Consumed
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-muted-foreground">
              TBD
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Requires image usage tracking
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
